import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkscoreShell from '../../components/workscore/WorkscoreShell';
import LanguageSelector from '../../components/ui/LanguageSelector';
import { getUIString } from '../../config/assessmentUIStrings';
import { getWorkscoreConfig } from '../../features/workscore/config';
import { startWorkscoreAttempt } from '../../features/workscore/api';
import { useWorkscoreStore } from '../../stores/workscoreStore';
import { translateStaticList, translateStaticText } from '../../config/staticAssessmentTranslations';

const RULES = [
  'This assessment uses a 250-question pool across 5 sections (50 per section).',
  'For this attempt, a random subset (10 or 12 questions per section) is selected from the pool.',
  'You cannot go back to a previous question once answered.',
  'You cannot skip questions. Each question must be answered before proceeding.',
  'You cannot navigate between sections freely. Complete each section fully to unlock the next.',
  'The assessment must be completed in full-screen mode. Exiting full-screen will be recorded as a violation.',
  'Switching browser tabs or minimizing the window is not allowed. 3 violations = auto-submission.',
  'Copy, paste, and right-click are disabled during the assessment.',
  'Do not use keyboard shortcuts (F12, Ctrl+Shift+I, etc.) during the assessment.',
  'All violations are logged and included in your final report.',
  'Once you submit a section, it is locked. You cannot return to it.',
  'Ensure a stable internet connection before starting.',
  'The assessment auto-saves your progress locally. If your browser crashes, you may resume from where you left off.'
];

export default function WorkscoreInstructionsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const config = getWorkscoreConfig(slug);
  const { startAssessment, setAttemptId, setQuestionSet } = useWorkscoreStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const isTranslating = false;

  if (!config) {
    return (
      <WorkscoreShell>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="rounded-2xl border border-white/15 bg-white/5 p-6 text-center">{translateStaticText('Assessment not found.', currentLanguage)}</div>
        </div>
      </WorkscoreShell>
    );
  }

  const t = (key, replacements = {}) => getUIString(currentLanguage, key, replacements);
  const displayRules = translateStaticList(RULES, currentLanguage);

  const handleLanguageChange = (langCode) => {
    setCurrentLanguage(langCode);
  };

  const handleBegin = async () => {
    const photo = sessionStorage.getItem(`${slug}_verify_photo`);
    if (!photo) {
      navigate(`/assessment/${slug}/verify`);
      return;
    }

    setError('');
    setLoading(true);
    try {
      startAssessment(slug, slug);
      const startPayload = await startWorkscoreAttempt({
        slug,
        profilePhoto: photo,
        questionsPerSection: config.questionsPerSection || 10
      });
      if (startPayload?.attemptId) {
        setAttemptId(startPayload.attemptId);
        sessionStorage.setItem(`workscore_attempt_${slug}`, startPayload.attemptId);
      }
      if (startPayload?.questionSet) {
        setQuestionSet(startPayload.questionSet, startPayload.questionsPerSection || config.questionsPerSection || 10);
      }
      navigate(`/workscore/${slug}/section/1`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to start assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkscoreShell>
      <div className="mx-auto max-w-4xl px-4 py-10 md:py-16">
        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 md:p-10">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl md:text-3xl font-semibold">{t('assessmentRules')}</h1>
            <LanguageSelector
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
              isTranslating={isTranslating}
            />
          </div>
          <ol className="mt-6 space-y-3 text-sm text-white/80 list-decimal pl-5">
            {displayRules.map((rule, index) => (
              <li key={`${index}_${rule}`} className={`transition-opacity duration-200 ${isTranslating ? 'opacity-40' : 'opacity-100'}`}>{rule}</li>
            ))}
          </ol>

          {error ? <p className="mt-5 text-sm text-red-300">{error}</p> : null}

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={loading}
              onClick={handleBegin}
              className="rounded-xl px-5 py-3 font-semibold text-white disabled:opacity-50"
              style={{ background: config.color }}
            >
              {loading ? 'Starting...' : t('beginAssessment')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/workscore/${slug}`)}
              className="rounded-xl border border-white/20 bg-white/10 px-5 py-3"
            >
              {translateStaticText('Go Back', currentLanguage)}
            </button>
          </div>
        </div>
      </div>
    </WorkscoreShell>
  );
}
